import React, {useCallback, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import isArray from 'lodash.isarray';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {ActivePlatformAlertsSortField} from 'src/modules/platform-alert-builder/all-alerts/typedef';
import {SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {SortHeaderFilter} from '@pb/kit-component-filters';


import {COLUMNS, HEADERS} from '../../constants';
import {activePlatformAlertsStateActions, activePlatformAlertsStateSelector} from '../../slices/active-platform-alerts-state.slice';

import {AlertTypeFilter} from './alert-type.filter';

import type {SortDirection} from 'src/openapi/admin/models/SortDirection';

export const AlertTypeHeader = () => {
    const dispatch = useDispatch();
    const cacheKeys = useSelector(activePlatformAlertsStateSelector);
    const [alertTypes, setAlertTypes] = useState(
        cacheKeys.filters.type,
    );

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({prev, current})) {
            setAlertTypes(current);
        }
    }, cacheKeys.filters.type);

    const sort = useCallback((direction: ('ASC' | 'DESC')) => {
        const sortCriteria = {
            direction: direction as SortDirection,
            field: ActivePlatformAlertsSortField.ALERT_TYPE,
        };

        dispatch(activePlatformAlertsStateActions.setSortCriteria(sortCriteria));
    }, [dispatch]);

    const applyFilter = useCallback(() => {
        const {type: _alertType, ...otherFilters} = cacheKeys.filters;
        const filters = {
            ...otherFilters,
            ...isArray(alertTypes) && alertTypes?.length ? {type: alertTypes} : undefined,
        };

        dispatch(activePlatformAlertsStateActions.setFilters(filters));
    }, [cacheKeys.filters, alertTypes, dispatch]);

    const clearFilter = useCallback(() => {
        const {type: _licenseType, ...filters} = cacheKeys.filters;

        dispatch(activePlatformAlertsStateActions.setFilters(filters));
    }, [cacheKeys.filters, dispatch]);

    const cancelFilter = useCallback(() => {
        setAlertTypes(cacheKeys.filters.type);
    }, [cacheKeys.filters]);

    return (
        <SortHeaderFilter
            filterActive={!!cacheKeys.filters?.type}
            sortable={true}
            caption={HEADERS[COLUMNS.ALERT_TYPE]}
            direction={cacheKeys.sortCriteria.field === ActivePlatformAlertsSortField.ALERT_TYPE
                ? cacheKeys.sortCriteria.direction
                : undefined}
            defaultDirection={SortDirections.ASC}
            onSort={sort}
            onApply={applyFilter}
            onReset={clearFilter}
            onCancel={cancelFilter}>
            <AlertTypeFilter
                selectedAlertTypes={alertTypes as string[]}
                onChange={setAlertTypes}
            />
        </SortHeaderFilter>
    );
};
